import {
  DataUpdatedOn,
  SkillsStackInputDataSources,
  Student,
  StudentBalance,
  StudentEvent,
  StudentRecommendation,
  StudentThirdPartyAppCredentials,
} from '@coach-bot/data-access/shared/models';
import { AbstractApiRequestService } from '@coach-bot/data-access/shared/services/abstract/abstract-api-request.service';
import StudentWorksmartData from '@coach-bot/features/tracking/models/student-worksmart-data';
import { Observable } from 'rxjs';
import {
  GET_DATA_UPDATED_ON_QUERY,
  GET_RECOMMENDATIONS_QUERY,
  GET_STUDENT_BALANCE_QUERY,
  GET_STUDENT_QUERY,
  GET_STUDENT_THIRD_PARTY_APP_CREDENTIALS_QUERY,
} from '../queries';
import {
  GET_STUDENT_WORKSMART_DATA_QUERY,
  GET_STUDENT_WORKSMART_DATA_QUERY_DEPRECATED,
} from '../queries/get-student-worksmart-data';
import { ON_POST_LEARNING_APP_MESSAGE_SUBSCRIPTION } from '../subscriptions';
import { convertPointsToMoney } from '../utils';

export class StudentService {
  constructor(private apiRequestService: AbstractApiRequestService) {}

  async getLastStudentLearningActivityTime(studentId: string): Promise<number | null> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getDataUpdatedOn: DataUpdatedOn }>(GET_DATA_UPDATED_ON_QUERY, { studentId })
      .then(response => response.getDataUpdatedOn)
      .then((data: DataUpdatedOn) => {
        return this.getLastStudentLearningActivityTimeValue(data);
      });
  }

  async getStudent(userId: string): Promise<Student> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentUser: Student }>(GET_STUDENT_QUERY, { userId })
      .then(response => response.getStudentUser);
  }

  async getStudentWorksmartData(): Promise<StudentWorksmartData> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentWorksmartData: StudentWorksmartData }>(GET_STUDENT_WORKSMART_DATA_QUERY)
      .then(response => response.getStudentWorksmartData);
  }

  async getStudentWorksmartDataDeprecated(studentId: string): Promise<StudentWorksmartData> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentWorksmartData: StudentWorksmartData }>(
        GET_STUDENT_WORKSMART_DATA_QUERY_DEPRECATED,
        {
          studentId,
        }
      )
      .then(response => response.getStudentWorksmartData);
  }

  async getStudentBalance(studentId: string): Promise<number> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentBalance: StudentBalance }>(GET_STUDENT_BALANCE_QUERY, { studentId })
      .then(response => convertPointsToMoney(response.getStudentBalance.amount));
  }

  async getRecommendations(
    studentId: string,
    subject: string,
    inputDataSources: SkillsStackInputDataSources,
    outputResourceTypes: string[],
    numberOfResources: number,
    usePlacementQuiz: boolean
  ): Promise<StudentRecommendation[]> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentRecommendations: StudentRecommendation[] }>(GET_RECOMMENDATIONS_QUERY, {
        studentId,
        subject,
        inputDataSources,
        outputResourceTypes,
        numberOfResources,
        usePlacementQuiz,
        externalStudentId: '',
        schoolId: '',
        studentEmail: '',
      })
      .then(response => response.getStudentRecommendations);
  }

  async getThirdPartyAppCredentials(appName: string): Promise<StudentThirdPartyAppCredentials> {
    return this.apiRequestService
      .getAppSyncQueryData<{ getStudentThirdPartyAppCredentials: StudentThirdPartyAppCredentials }>(
        GET_STUDENT_THIRD_PARTY_APP_CREDENTIALS_QUERY,
        { appName }
      )
      .then(response => response.getStudentThirdPartyAppCredentials);
  }

  subscribeToStudentEvents(studentId: string): Observable<StudentEvent> {
    return this.apiRequestService.subscribe(ON_POST_LEARNING_APP_MESSAGE_SUBSCRIPTION, { studentId });
  }

  private getLastStudentLearningActivityTimeValue(data: DataUpdatedOn): number | null {
    const lastLearningSessionTime: number | null = data.learningSessions
      ? new Date(data.learningSessions).getTime()
      : null;
    const topicMasteryTime: number | null = data.topicMastery ? new Date(data.topicMastery).getTime() : null;
    if (!lastLearningSessionTime && !topicMasteryTime) {
      return null;
    }
    if (!lastLearningSessionTime) {
      return topicMasteryTime;
    }
    if (!topicMasteryTime) {
      return lastLearningSessionTime;
    }
    return Math.max(lastLearningSessionTime, topicMasteryTime);
  }
}
